/**
 * Dónde estoy y cuánto falta (GKTIBO-81).
 *
 * El alta tenía tres pantallas y ninguna decía cuántas eran. Sin eso, cada paso
 * se siente como el último posible: no hay forma de saber si falta uno o cinco,
 * y esa incertidumbre es lo que hace abandonar a mitad de camino.
 *
 * Muestra los pasos **que el servidor dice**, no una lista fija: el estado del
 * alta lo calcula el backend y duplicarlo acá era tener dos versiones de la
 * verdad que divergen.
 */

import type { IdDePaso, PasoDelAlta } from './EstadoDelAlta';
import { textos } from './textos';

interface PasosDelAltaProps {
  pasos: PasoDelAlta[];
  activo: IdDePaso;
}

export function PasosDelAlta({ pasos, activo }: PasosDelAltaProps) {
  /**
   * Se muestran los tres, `identidad` incluido (GKTIBO-120).
   *
   * Antes se escondía, con el argumento de que no tiene pantalla propia y el
   * dueño vería «paso 2 de 4» sin haber visto la primera. Con cuatro pasos eso
   * era cierto; con tres ya no, y **esconderlo cuesta más de lo que ahorra**:
   * el registro SÍ es un paso que él hizo —puso el nombre de su negocio—, y
   * verlo tildado es lo que le dice que el alta arrancó bien.
   */
  const visibles = pasos;

  return (
    <nav className="alta-pasos" aria-label={textos.pasos.titulo} data-testid="alta-pasos">
      <ol className="alta-pasos-lista">
        {visibles.map((paso, i) => {
          const esActivo = paso.id === activo;
          const estado = esActivo ? 'activo' : paso.completo ? 'completo' : 'pendiente';
          return (
            <li
              key={paso.id}
              className={`alta-paso-item alta-paso-${estado}`}
              data-testid={`alta-paso-${paso.id}`}
              // Lo lee un lector de pantalla y lo usa el test: el estado no
              // puede vivir sólo en el color.
              aria-current={esActivo ? 'step' : undefined}
              data-estado={estado}
            >
              <span className="alta-paso-numero" aria-hidden="true">
                {paso.completo && !esActivo ? '✓' : i + 1}
              </span>
              <span className="alta-paso-nombre">{textos.pasos.nombres[paso.id]}</span>
            </li>
          );
        })}
      </ol>
      <p className="alta-pasos-cuenta">
        {textos.pasos.cuenta(visibles.findIndex((p) => p.id === activo) + 1, visibles.length)}
      </p>
    </nav>
  );
}
